import { FormikProps, Field } from 'formik';
import * as React from 'react';
import { Element } from 'react-scroll';
import * as Yup from 'yup';
import {
  SubForm,
  FormSection,
  FormCheckBox,
  Heading,
  Box,
  Icon,
} from '@alinta-digital/asg';
import * as icons from '@alinta-digital/asg/icons/';
import { FuelSite } from '../../types';
import { submit } from '../../components/Review';
import { FormContainerContext } from '../../contexts/FormContainerContext';
import {
  setFormContextError,
  scrollToScrollElement,
  resetFormContextError,
} from '../../utils/utils';
import { ErrorNotification } from '../../components/ErrorNotification';
import { DirectDebitForm } from '../../views/Form/Form';
import { ButtonV2, StyledLink, StyledText } from '../../Common.Styled';

interface ReviewSubForm {
  consent: boolean;
}

export interface ReviewParentForm {
  review: ReviewSubForm;
}

interface ReviewSubFormProps {
  formikProps: FormikProps<DirectDebitForm>;
  isEdit: boolean;
  onPreviousClick: () => void;
  onNextClick: () => void;
}

export const ReviewSubForm: React.FC<ReviewSubFormProps> = ({
  formikProps,
  onPreviousClick,
  onNextClick,
}) => {
  const {
    identifyYourAccountForm,
    connection,
    debitDetails,
    review,
  } = formikProps.values;
  const { electricity: parentFormElectricity, gas: parentFormGas } = connection;
  const accountType =
    parentFormGas?.[0]?.accountType || parentFormElectricity?.[0]?.accountType;
  const tncResidentialUrl = 'https://www.alintaenergy.com.au/directdebit-tcs';
  const tncBusinessUrl =
    'https://www.alintaenergy.com.au/nsw/-/jssmedia/alinta-website/documents/sme/22-024-direct-debit-form-sme-online.pdf';
  const tncDownloadUrl =
    accountType?.toLocaleLowerCase() === 'business'
      ? tncBusinessUrl
      : accountType?.toLocaleLowerCase() === 'residential'
      ? tncResidentialUrl
      : '';

  const {
    values: formContextValues,
    dispatch: formContextDispatch,
  } = React.useContext(FormContainerContext);
  const { error: formContextError } = formContextValues;

  const [submitLoading, setSubmitLoading] = React.useState(false);

  React.useEffect(() => {
    if (formContextError.steps[3].status) {
      scrollToScrollElement('notificationError');
    } else {
      scrollToScrollElement('reviewStart');
    }
  });

  return (
    <SubForm<ReviewParentForm, ReviewSubForm>
      formikProps={formikProps}
      config={{
        review: {
          consent: {
            validation: Yup.string()
              .required('Need to read and agree')
              .test(
                'validation',
                'Need to read and agree',
                (userInput: string) => {
                  if (userInput === 'true') {
                    return true;
                  }
                  return false;
                }
              ),
            initialValue: review.consent,
          },
        },
      }}
      valuesKey="review"
    >
      {({ validateSubForm, subFormFormikProps }) => (
        <FormSection
          id="Review"
          heading="Step 4: Review and confirm"
          stepNumber="4"
          totalSteps="4"
          previousButton={
            !formContextError.steps[3].status ? (
              <ButtonV2
                id="reviewPreviousButton"
                data-testid="review_previousButton"
                disabled={submitLoading}
                secondary={true}
                type="button"
                onClick={() => {
                  resetFormContextError(
                    formContextValues,
                    formContextDispatch,
                    3
                  );
                  onPreviousClick();
                }}
              >
                Back
              </ButtonV2>
            ) : (
              undefined
            )
          }
          nextButton={
            !formContextError.steps[3].status ? (
              <ButtonV2
                id="reviewNextButton"
                data-testid="review_nextButton"
                disabled={!subFormFormikProps.values.review.consent}
                loading={submitLoading}
                primary={true}
                type="button"
                onClick={async () => {
                  setSubmitLoading(true);
                  const { isValid } = await validateSubForm();

                  if (!isValid) {
                    setSubmitLoading(false);
                    return false;
                  }

                  formikProps.setValues({
                    ...formikProps.values,
                    ...subFormFormikProps.values,
                  });

                  try {
                    const result = await submit(
                      identifyYourAccountForm.accountNumber,
                      debitDetails.bankAccountName,
                      debitDetails.bsbNumber,
                      debitDetails.bankAccountNumber,
                      [...parentFormElectricity, ...parentFormGas]
                        .map((fuel: FuelSite) => fuel.accountNumber)
                        .filter((accountNumber: string) =>
                          accountNumber ? true : false
                        ),
                      formContextValues.bankBranch.displayName
                    );

                    if (result.status === 200) {
                      onNextClick();
                      return true;
                    }

                    throw result;
                  } catch (error) {
                    setSubmitLoading(false);
                    setFormContextError(
                      formContextValues,
                      formContextDispatch,
                      3,
                      true,
                      'Submission error'
                    );
                  }
                }}
              >
                Setup Direct Debit
              </ButtonV2>
            ) : (
              undefined
            )
          }
        >
          {formContextError.steps[3].status ? (
            <ErrorNotification
              errorType={formContextError.steps[3].type}
              stateShortName={formContextValues.stateShortName}
              onClick={() =>
                setFormContextError(
                  formContextValues,
                  formContextDispatch,
                  3,
                  false,
                  undefined
                )
              }
            />
          ) : (
            <>
              <Element name="reviewStart" id="reviewStart" />
              <Box margin={{ bottom: '16px' }}>
                <Heading level={3} color="#000F20" weight={700}>
                  {parentFormElectricity.length && parentFormGas.length
                    ? 'Electricity & gas'
                    : parentFormElectricity.length
                    ? 'Electricity'
                    : 'Gas'}
                </Heading>
              </Box>
              <Box margin={{ bottom: 'small' }}>
                <StyledText lineHeight="21px" data-testid="review_text">
                  You&apos;re almost there. Just confirming that you&apos;d like
                  us to setup direct debit for the following Alinta Energy
                  account by Bank Transfer, to continue, click on the Setup
                  Direct Debit Button.
                  {parentFormElectricity.map((site: FuelSite) => (
                    <div
                      key={site.authAccountNumber}
                      data-testid="selectedAccounts"
                    >
                      {`${site.authAccountNumber} Electricity `}
                    </div>
                  ))}
                  {parentFormGas.map((site: FuelSite) => (
                    <div
                      key={site.authAccountNumber}
                      data-testid="selectedAccounts"
                    >
                      {`${site.authAccountNumber} Gas `}
                    </div>
                  ))}
                </StyledText>
              </Box>
              <Box margin={{ bottom: '14px' }}>
                <Field
                  component={FormCheckBox}
                  name="review.consent"
                  id="review.consent"
                  data-testid="consent_checkBox"
                  label={
                    <StyledText>
                      I authorise Alinta Energy Retail Sales Pty Ltd ABN 22 149
                      658 300 (User ID No. 398218), until further notice, to
                      debit my nominated account through the Direct Debit system
                      for amounts due, in accordance with the{' '}
                      <StyledLink
                        icon={
                          <Icon
                            id="externalLinkIcon"
                            icon={icons.ExternalLink}
                            css={{
                              height: '16px !important',
                              width: '16px !important',
                              marginBottom: '-1px !important',
                            }}
                          />
                        }
                        download
                        iconAlign="right"
                        href={tncDownloadUrl}
                      >
                        Direct Debit Service Agreement
                      </StyledLink>
                      I understand that the amount debited may vary and will
                      include any applicable discounts.
                    </StyledText>
                  }
                />
              </Box>
            </>
          )}
        </FormSection>
      )}
    </SubForm>
  );
};
